/**
 * Denylist of HITL contract ids that must never be re-seeded as a durable
 * in-chat gate card. Backed by an in-memory Set (fast path) and mirrored to
 * `localStorage` so a page reload does not resurrect a card the user already
 * handled.
 *
 * Why this exists: **a pending task row can outlive its workflow.** A terminated
 * Temporal run leaves its task `status: 'pending'` in the task table
 * indefinitely (the workflow dies; the row does not), so the durable-restore
 * path (`selectDurableHitlTask`) keeps resurrecting the same zombie card and
 * reconcile-by-pending-list (`isCardStillPending`) can never clear it — the row
 * is genuinely still "pending".
 *
 * A contract lands here when a resolve attempt proves the workflow is gone
 * (`already_resolved` 404/409, `resume_conflict` 502 HITL_ERR_RESUME_FAILED),
 * when the card is resolved for real, or when the user manually dismisses it.
 * Persisting to `localStorage` keeps those decisions across reloads; contract
 * ids are unique per gate occurrence, so a stored id can never collide with a
 * genuinely new gate.
 *
 * Framework-free: in a non-browser context (SSR, Node tests) storage is absent
 * and it degrades to the in-memory Set only.
 */

const STORAGE_KEY = 'aimsos.hitl.dismissedContracts';
// Cap persisted history so the key can't grow without bound; the most recent
// ids are the ones a fallback poll could still try to resurface.
const MAX_PERSISTED = 500;

/** @returns {Storage|null} localStorage when usable, else null (guards private-mode throws). */
const getStore = () => {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
};

const dismissed = new Set(hydrate());

/** Read the persisted ids once at module load. Never throws. */
function hydrate() {
  const store = getStore();
  if (!store) return [];
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string' && id) : [];
  } catch {
    return [];
  }
}

/** Mirror the Set (capped, most-recent-last) back to storage. Never throws. */
const persist = () => {
  const store = getStore();
  if (!store) return;
  try {
    const ids = Array.from(dismissed);
    const capped = ids.length > MAX_PERSISTED ? ids.slice(ids.length - MAX_PERSISTED) : ids;
    store.setItem(STORAGE_KEY, JSON.stringify(capped));
  } catch {
    /* storage full or blocked — the in-memory Set still guards this session */
  }
};

/**
 * Record a contract as dismissed so the durable-restore path stops re-seeding
 * it, now and after a reload.
 * @param {string|null|undefined} contractId
 */
export const markHitlContractDismissed = (contractId) => {
  if (!contractId) return;
  dismissed.add(contractId);
  // Trim oldest entries (Sets keep insertion order) to honor the cap in memory.
  while (dismissed.size > MAX_PERSISTED) {
    const oldest = dismissed.values().next().value;
    dismissed.delete(oldest);
  }
  persist();
};

/**
 * @param {string|null|undefined} contractId
 * @returns {boolean} true when the contract is on the denylist.
 */
export const isHitlContractDismissed = (contractId) =>
  Boolean(contractId) && dismissed.has(contractId);

/** Test-only: clear the denylist (memory + storage) between cases. */
export const _resetDismissedHitlContracts = () => {
  dismissed.clear();
  const store = getStore();
  try {
    store?.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};
